// src/components/common/Hero.tsx
import React from 'react';
import Icon from './Icon';
import { IconName } from '../../types';

interface HeroStat {
    value: string;
    label: string;
}

interface HeroProps {
    eyebrow?: string;
    eyebrowIcon?: IconName;
    title: React.ReactNode;
    description: React.ReactNode;
    stats?: HeroStat[];
    art?: 'documents' | 'chart' | 'house' | 'family';
}

/* 카테고리 성격에 맞춘 추상 일러스트 (외부 이미지 없이 인라인 SVG) */
const HeroArt: React.FC<{ variant: NonNullable<HeroProps['art']> }> = ({ variant }) => {
    const c = {
        deep: '#1D4ED8',
        mid: '#3B82F6',
        soft: '#93C5FD',
        pale: '#DBEAFE',
        white: '#FFFFFF',
    };

    if (variant === 'chart') {
        return (
            <svg viewBox="0 0 260 170" role="presentation">
                <rect x="26" y="26" width="150" height="118" rx="10" fill={c.white} opacity=".92" />
                <rect x="26" y="26" width="150" height="26" rx="10" fill={c.pale} />
                <rect x="26" y="42" width="150" height="10" fill={c.pale} />
                <circle cx="40" cy="39" r="3.4" fill={c.soft} />
                <circle cx="51" cy="39" r="3.4" fill={c.soft} />
                <rect x="44" y="106" width="17" height="24" rx="3.5" fill={c.soft} />
                <rect x="69" y="92" width="17" height="38" rx="3.5" fill={c.mid} />
                <rect x="94" y="78" width="17" height="52" rx="3.5" fill={c.mid} />
                <rect x="119" y="60" width="17" height="70" rx="3.5" fill={c.deep} />
                <rect x="144" y="46" width="17" height="84" rx="3.5" fill={c.deep} />
                <path d="M52 98 L77 84 L102 70 L127 52 L152 38" stroke={c.white} strokeWidth="3" fill="none" strokeLinecap="round" opacity=".85" />
                <circle cx="196" cy="62" r="30" fill={c.pale} />
                <path d="M196 32a30 30 0 0 1 30 30h-30z" fill={c.deep} />
                <path d="M196 62h30a30 30 0 0 1-21 28.6z" fill={c.mid} />
                <rect x="176" y="112" width="62" height="9" rx="4.5" fill={c.soft} opacity=".7" />
                <rect x="176" y="128" width="44" height="9" rx="4.5" fill={c.soft} opacity=".45" />
            </svg>
        );
    }

    if (variant === 'house') {
        return (
            <svg viewBox="0 0 260 170" role="presentation">
                <rect x="18" y="74" width="58" height="72" rx="6" fill={c.soft} opacity=".65" />
                <rect x="28" y="88" width="12" height="12" rx="2.5" fill={c.white} />
                <rect x="48" y="88" width="12" height="12" rx="2.5" fill={c.white} />
                <rect x="28" y="110" width="12" height="12" rx="2.5" fill={c.white} />
                <rect x="48" y="110" width="12" height="12" rx="2.5" fill={c.white} />
                <path d="M86 78 L130 44 L174 78" fill="none" stroke={c.deep} strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" />
                <rect x="94" y="78" width="72" height="68" rx="6" fill={c.white} />
                <rect x="94" y="78" width="72" height="68" rx="6" fill="none" stroke={c.mid} strokeWidth="2.5" />
                <rect x="106" y="94" width="20" height="18" rx="3" fill={c.pale} />
                <rect x="134" y="94" width="20" height="18" rx="3" fill={c.pale} />
                <rect x="118" y="122" width="24" height="24" rx="3" fill={c.mid} />
                <rect x="184" y="92" width="58" height="54" rx="6" fill={c.soft} opacity=".55" />
                <rect x="194" y="104" width="11" height="11" rx="2.5" fill={c.white} />
                <rect x="212" y="104" width="11" height="11" rx="2.5" fill={c.white} />
                <rect x="194" y="122" width="11" height="11" rx="2.5" fill={c.white} />
                <rect x="212" y="122" width="11" height="11" rx="2.5" fill={c.white} />
                <rect x="10" y="146" width="240" height="6" rx="3" fill={c.pale} />
            </svg>
        );
    }

    if (variant === 'family') {
        return (
            <svg viewBox="0 0 260 170" role="presentation">
                <circle cx="196" cy="52" r="42" fill={c.pale} opacity=".8" />
                <circle cx="86" cy="60" r="17" fill={c.mid} />
                <path d="M60 138c0-16 12-27 26-27s26 11 26 27z" fill={c.mid} />
                <circle cx="140" cy="52" r="20" fill={c.deep} />
                <path d="M110 138c0-19 13-32 30-32s30 13 30 32z" fill={c.deep} />
                <circle cx="196" cy="82" r="12" fill={c.soft} />
                <path d="M178 138c0-11 8-19 18-19s18 8 18 19z" fill={c.soft} />
                <rect x="30" y="138" width="200" height="7" rx="3.5" fill={c.pale} />
                <path d="M214 30c4-9 18-7 18 3 0 8-11 14-18 19-7-5-18-11-18-19 0-10 14-12 18-3z" fill={c.white} opacity=".95" />
            </svg>
        );
    }

    return (
        <svg viewBox="0 0 260 170" role="presentation">
            <rect x="34" y="20" width="112" height="132" rx="9" fill={c.soft} opacity=".45" transform="rotate(-7 90 86)" />
            <rect x="52" y="18" width="112" height="132" rx="9" fill={c.white} />
            <rect x="52" y="18" width="112" height="132" rx="9" fill="none" stroke={c.pale} strokeWidth="2" />
            <rect x="68" y="38" width="58" height="9" rx="4.5" fill={c.deep} />
            <rect x="68" y="58" width="80" height="7" rx="3.5" fill={c.pale} />
            <rect x="68" y="73" width="80" height="7" rx="3.5" fill={c.pale} />
            <rect x="68" y="88" width="56" height="7" rx="3.5" fill={c.pale} />
            <rect x="68" y="110" width="34" height="20" rx="5" fill={c.mid} />
            <circle cx="196" cy="106" r="34" fill={c.deep} />
            <circle cx="196" cy="106" r="26" fill="none" stroke={c.white} strokeWidth="2.5" opacity=".55" />
            <path d="M196 90v32M204 96.5c-1.6-3.4-4.6-5.2-8-5.2-4.4 0-8 2.7-8 6.3s3 5.4 8 6.3 8 2.7 8 6.3-3.6 6.3-8 6.3c-3.4 0-6.4-1.8-8-5.2" stroke={c.white} strokeWidth="3.4" fill="none" strokeLinecap="round" />
            <circle cx="214" cy="48" r="15" fill={c.soft} opacity=".8" />
            <path d="m208 48 4.4 4.4L221 44" stroke={c.white} strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
};

const Hero: React.FC<HeroProps> = ({
    eyebrow,
    eyebrowIcon = 'sparkle',
    title,
    description,
    stats,
    art = 'documents',
}) => (
    <section className="gh-hero">
        <div className="gh-hero__content">
            {eyebrow && (
                <span className="gh-hero__eyebrow">
                    <Icon name={eyebrowIcon} size={13} strokeWidth={2} />
                    {eyebrow}
                </span>
            )}
            <h1 className="gh-hero__title">{title}</h1>
            <p className="gh-hero__desc">{description}</p>

            {stats && stats.length > 0 && (
                <div className="gh-hero__stats">
                    {stats.map(s => (
                        <div className="gh-hero__stat" key={s.label}>
                            <b>{s.value}</b>
                            <span>{s.label}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>

        <div className="gh-hero__art" aria-hidden="true">
            <HeroArt variant={art} />
        </div>
    </section>
);

export default Hero;
